import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Code } from 'src/design/atoms/Code';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Select } from 'src/design/atoms/Select';
import { DatesRangeValue } from '@mantine/dates';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo, useState } from 'react';
import { SqlToolAuditDto } from 'src/api/FiretrolApiClient';
import { useAdminSqlAudit } from 'src/api/queries/chatAdmin/useAdminSqlAudit';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { AnchorLink } from 'src/design/molecules/AnchorLink';
import { PAGE_SIZE } from 'src/routes/Settings/AIAssistant/constants';
import { formatDateTime } from 'src/utils';

export interface ISqlAuditTableProps {
  /** Fixed filter — when set, the User column is hidden and every request is scoped to this user. */
  userId?: number;
  dateRange?: DatesRangeValue;
}

const getColumns = (
  showUserColumn: boolean,
): MRT_ColumnDef<SqlToolAuditDto>[] => {
  const userColumn: MRT_ColumnDef<SqlToolAuditDto> = {
    accessorKey: 'userName',
    header: 'User',
    Cell: ({ cell, row }) => (
      <AnchorLink to={`/settings/ai-assistant/users/${row.original.userId}`}>
        {cell.getValue<string>()}
      </AnchorLink>
    ),
  };

  const rest: MRT_ColumnDef<SqlToolAuditDto>[] = [
    {
      accessorKey: 'validationResult',
      header: 'Validation',
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return (
          <Badge color={value === 'accepted' ? 'green' : 'red'} variant="light">
            {value}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'executed',
      header: 'Executed',
      Cell: ({ cell }) => (cell.getValue<boolean>() ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'rowCount',
      header: 'Rows',
    },
    {
      accessorKey: 'truncated',
      header: 'Truncated',
      Cell: ({ cell }) => (cell.getValue<boolean>() ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'durationMs',
      header: 'Duration (ms)',
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
    },
  ];

  return showUserColumn ? [userColumn, ...rest] : rest;
};

export const SqlAuditTable: React.FC<ISqlAuditTableProps> = ({
  userId,
  dateRange,
}) => {
  const [validationFilter, setValidationFilter] = useState<string | null>(null);
  const [from, to] = dateRange ?? [undefined, undefined];

  const currentAccepted =
    validationFilter === 'accepted'
      ? true
      : validationFilter === 'rejected'
      ? false
      : undefined;

  const auditQuery = useAdminSqlAudit({
    userId,
    from: from ?? undefined,
    to: to ?? undefined,
    accepted: currentAccepted,
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const audits = auditQuery.data?.items ?? [];

  const columns = useMemo(() => getColumns(!userId), [userId]);

  const tableState = useTableState({
    identifier: userId
      ? 'ai-assistant-user-sql-audit'
      : 'ai-assistant-sql-audit',
    columns,
    defaultSorting: [{ id: 'createdAt', desc: true }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns,
      data: audits,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderDetailPanel: ({ row }) => (
        <Stack gap={4}>
          <Box style={{ overflowX: 'auto' }}>
            <Code block>{row.original.generatedSql}</Code>
          </Box>
          {row.original.error && (
            <Box style={{ overflowX: 'auto' }}>
              <Code block color="red">
                {row.original.error}
              </Code>
            </Box>
          )}
        </Stack>
      ),
      renderTopToolbarCustomActions: () => (
        <Group>
          <Select
            placeholder="Validation result"
            clearable
            data={[
              { value: 'accepted', label: 'Accepted' },
              { value: 'rejected', label: 'Rejected' },
            ]}
            value={validationFilter}
            onChange={setValidationFilter}
          />
        </Group>
      ),
    },
    getRowId: (row) => row.id,
    totalCount: audits.length,
    isLoading: auditQuery.isLoading,
    isFetching: auditQuery.isFetching,
    isError: auditQuery.isError,
    onRefresh: auditQuery.refetch,
  });

  return table;
};
